
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { CreateUserData, StaffManagedUser } from './types';


// Fetch all users that staff can manage (excludes other staff and admins)
export const fetchStaffManagedUsers = async (): Promise<StaffManagedUser[]> => {
  try {
    console.log('Fetching staff managed users...');

    // 1) Primary: enhanced managed users RPC (ideal, log-independent)
    const { data: enhanced, error: enhancedError } = await supabase.rpc('get_staff_managed_users_enhanced');
    if (enhancedError) {
      console.warn('Enhanced RPC failed, will try fallbacks:', enhancedError);
    }

    const mapToManaged = (rows: any[] | null | undefined): StaffManagedUser[] =>
      (rows || [])
        .filter((u: any) => (u.role ?? 'user') === 'user')
        .map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          role: 'student' as const,
          is_approved: u.is_approved,
          created_at: u.created_at,
          avatar_url: u.avatar_url
        }));

    let users: StaffManagedUser[] = mapToManaged(enhanced);

    // 2) Fallback A: staff-created users (log-based)
    if ((!users || users.length === 0)) {
      const { data: createdUsers, error: createdErr } = await supabase.rpc('get_staff_created_users');
      if (createdErr) console.warn('Fallback get_staff_created_users failed:', createdErr);
      users = mapToManaged(createdUsers);
    }

    // 3) Fallback B: admin-wide listing (filtered client-side to students)
    if ((!users || users.length === 0)) {
      const { data: allUsers, error: allErr } = await supabase.rpc('admin_get_all_users');
      if (allErr) console.warn('Fallback admin_get_all_users failed:', allErr);
      users = mapToManaged(allUsers);
    }

    console.log('Processed staff managed users:', users);
    return users;
  } catch (error) {
    console.error('Error in fetchStaffManagedUsers:', error);
    toast.error('Failed to fetch users');
    return [];
  }
};

// Create a new user (staff can only create students)
export const createUserByStaff = async (
  userData: CreateUserData, 
  sendWelcomeEmail: boolean = true
): Promise<boolean> => {
  try {
    console.log('Staff creating user:', userData);

    console.log('Staff invoking invite-user edge function with:', {
      email: userData.email,
      name: userData.name,
      role: 'user',
      sendWelcomeEmail
    });

    // Use the invite-user edge function like the admin portal does
    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: {
        email: userData.email,
        name: userData.name,
        role: 'user', // Staff can only create students
        sendWelcomeEmail // Use parameter instead of hardcoded true
      }
    });

    if (error) {
      console.error('Edge function error details:', {
        error,
        message: error?.message,
        details: error?.details,
        status: error?.status,
        stack: error?.stack,
        context: 'Staff user creation'
      });
      
      // Handle specific error cases for staff
      let errorMessage = 'Failed to create user';
      
      if (typeof error === 'object' && error !== null) {
        if ('message' in error && error.message) {
          const msg = error.message as string;
          if (msg.includes('Insufficient permissions')) {
            errorMessage = 'Staff members can only create student accounts';
          } else if (msg.includes('duplicate') || msg.includes('already')) {
            errorMessage = 'A user with this email already exists';
          } else {
            errorMessage = msg;
          }
        }
      }
      
      toast.error(errorMessage);
      return false;
    }

    // Check if the edge function returned success
    if (data?.success) {
      toast.success(data.message || 'User created successfully');
      
      // Log the staff action for audit purposes
      // Logging handled by edge function; no client-side log
      
      return true;
    } else {
      toast.error(data?.message || 'Failed to create user');
      return false;
    }
  } catch (error) {
    console.error('Error creating user:', error);
    toast.error('Failed to create user');
    return false;
  }
};

// Update user profile using security definer function
export const updateUserByStaff = async (
  userId: string, 
  updates: { name?: string; email?: string; is_approved?: boolean }
): Promise<boolean> => {
  try {
    console.log('Staff updating user:', userId, updates);

    // Use the security definer RPC function for safe staff updates
    const { data, error } = await supabase.rpc('staff_update_user_safe', {
      target_user_id: userId,
      user_name: updates.name || null,
      user_email: updates.email || null,
      is_approved: updates.is_approved
    });

    if (error) {
      console.error('Error updating user via RPC:', error);
      toast.error('Failed to update user: ' + error.message);
      return false;
    }

    // Parse the response data as an object
    const result = data as { success?: boolean; message?: string; error?: string } | null;
    
    if (result?.success) {
      toast.success(result.message || 'User updated successfully');
      return true;
    } else {
      toast.error(result?.error || 'Failed to update user');
      return false;
    }
  } catch (error) {
    console.error('Error updating user:', error);
    toast.error('Failed to update user');
    return false;
  }
};
